import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../components/EditProductDescription/EditProductDescription.css";
import { BiLaptop, BiMobile } from "react-icons/bi";
import Breadcrumb from "../../components/Common/Bredcrump";
import { generateProductDescription } from "../../utils/Product";
import Loader from "../../components/Common/Loader";
import EmailDetailsContainer from "../../components/Email/EmailDetailsContainer";
import EmailHeaderImage from "../../components/Email/EmailHeaderImage";
import AddEmailSidebar from "../../components/Email/AddEmailSidebar";
import { emailTemplateHTML } from "./EmailTemplateHtml";
import { manageAgentSessionIds } from "../../helpers";
import axiosInstance from "../../utils/api";
import { ChromePicker } from 'react-color';
import { Dropdown } from 'react-bootstrap';
import { Settings, X } from 'lucide-react';
import { EmailSectionProvider, useEmailSections } from "../../components/Context/EmailSectionContext";
import { ToastContainer, toast } from 'react-toastify';
import LocalDraftModal from "../../components/EditProductDescription/LocalDraftModal";
import { cleanImageValue } from "../../utils/imageHelper";

// Wrapper component that lives inside the EmailSectionProvider

const EmailContent = ({
  primaryColor,
  secondaryColor,
  backgroundColor,
  fontFamily,
  onSaveDraft,
  onPreview,
  emailData,
  isLoading,
  onUpdateEmailData,
  draftSaved 
}) => {
  console.log("EmailContent emailData: ", emailData);
  // This hook call is now INSIDE the provider context scope
  const { sections } = useEmailSections();

  useEffect(() => {
    console.log("EmailContent sections from context:", sections);
  }, [sections]);

  // Method to handle saving draft with access to sections
  const handleSave = () => {
    console.log("EmailContent saving with sections:", sections);
    if (onSaveDraft) onSaveDraft(sections);
  };

  // Method to handle preview with access to sections
  const handlePreview = (mode) => {
    console.log("EmailContent previewing with sections:", sections);
    if (onPreview) onPreview(sections, mode);
  };

  return (
    <div className="col position-relative" style={{ minHeight: "500px", backgroundColor: 'rgba(208, 71, 52, 0.04)' }}>
      {isLoading && (
        <div className="position-absolute start-50 translate-middle d-flex justify-content-center align-items-center z-3"
          style={{
            top: "10%",
            width: "100%",
            height: "200%",
            background: "rgba(0, 0, 0, 0.47)",
            backdropFilter: "blur(5px)",
          }}
        >
          <Loader />
        </div>
      )}
      <>
        <div className="d-flex gap-4 align-items-center" >
            <h5 style={{ fontWeight: "500", fontSize: "16px" }}>
              Generated Email
            </h5>
          <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">

            <h6 className="mt-2 mx-1">Preview</h6>
            <div className="d-flex justify-content-center align-items-center">
              <button className="btn" onClick={() => handlePreview("desktop")}>
                <BiLaptop size={20} />
              </button> |
              <button className="btn" onClick={() => handlePreview("mobile")}>
                <BiMobile size={20} />
              </button>
            </div>

          </div>
          <div className="btns d-flex justify-content-end gap-3 ">
            <div>
              <button className="btn bgprimary rounded" onClick={handleSave}>Save as draft</button>
            </div>
          </div>
        </div>
        <EmailHeaderImage
          initialHeroImage={emailData?.heroImage}
          onHeroImageChange={(img) => onUpdateEmailData && onUpdateEmailData({
            heroImage: img
          })}
        />
        <EmailDetailsContainer
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          backgroundColor={backgroundColor}
          fontFamily={fontFamily}
          onUpdateEmailData={onUpdateEmailData}
        />

      </>
    </div>
  );
};

const EditEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [initialSections, setInitialSections] = useState(location.state?.emailData || []);
  const initialSku = location.state?.sku || "";
  const initialProductData = location.state?.productData || null;
  const initialTags = Array.isArray(location.state?.tags) ? location.state.tags : [];
  // const templateName = location.state?.sku?.replace(/_/g, " ")?.trim()?.replace(/\s+/g, " ") || "";
  const previewUrl = location.state?.previewUrl || "";
  const templateId = location.state?.templateId || "";
  const uid = location.state?.uid || "";
  // No longer need this at the top level
  // const { sections } = useEmailSections();

  const [hasAutoSaved, setHasAutoSaved] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isLoading, setIsLoading] = useState(!initialProductData);
  const [productData, setProductData] = useState(initialProductData);
  const [error, setError] = useState(null);
  const [keywords, setKeywords] = useState(initialTags);
  const [details, setDetails] = useState(initialProductData?.details || "");
  const [ingredients, setIngredients] = useState("");
  const [howToUse, setHowToUse] = useState("");
  const [previewMode, setPreviewMode] = useState(location.state?.mode || "desktop");
  const [occasion, setOccasion] = useState(location.state?.occasion || "");
  const [furtherDetails, setFurtherDetails] = useState(location.state?.furtherDetails || "");
  const [tone, setTone] = useState(location.state?.tone || "");
  const previewHtml = location.state?.previewHtml || "";
  const [previewImage, setPreviewImage] = useState(location.state?.previewImage || "");
  const [templateName, setTemplateName] = useState(location.state?.templateName || "");
  // Styling options
  const [primaryColor, setPrimaryColor] = useState(location.state?.primaryColor || "#333333");
  const [secondaryColor, setSecondaryColor] = useState(location.state?.secondaryColor || "#a1894c");
  const [backgroundColor, setBackgroundColor] = useState(location.state?.backgroundColor || "#ffffff");
  const [fontFamily, setFontFamily] = useState(location.state?.fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [showStyleSettings, setShowStyleSettings] = useState(false);
  const [showLocalDraftModal, setShowLocalDraftModal] = useState(false);

  // Font options
  const fontOptions = [
    { name: "Helvetica", value: "helvetica, 'helvetica neue', arial, verdana, sans-serif" },
    { name: "Arial", value: "arial, sans-serif" },
    { name: "Georgia", value: "georgia, times, serif" },
    { name: "Verdana", value: "verdana, geneva, sans-serif" },
    { name: "Tahoma", value: "tahoma, geneva, sans-serif" },
    { name: "Trebuchet MS", value: "trebuchet ms, helvetica, sans-serif" },
    { name: "Times New Roman", value: "times new roman, times, serif" }
  ];

  // Email data state - supports both array (from context) and object (legacy) formats
  const [emailData, setEmailData] = useState(() => {
    const stateData = location.state?.emailData;
    // If location state has array data, use it directly
    if (Array.isArray(stateData)) {
      return stateData;
    }
    // Otherwise use legacy object format
    return {
      header_section: {},
      offer_details: {},
      featured_products: { features: [] },
      ...(stateData || {})
    };
  });

  // Refs for clicking outside to close color picker
  const colorPickerRef = useRef(null);
  const styleSettingsRef = useRef(null);

  // Handle clicking outside to close the color picker
  useEffect(() => {
    function handleClickOutside(event) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
      if (styleSettingsRef.current && !styleSettingsRef.current.contains(event.target) &&
        !event.target.closest('.style-settings-toggle')) {
        setShowStyleSettings(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Function to update emailData from child components
  const handleUpdateEmailData = useCallback((updatedData) => {
    console.log("handleUpdateEmailData called with:", updatedData);

    // If updatedData is an array (from EmailSectionContext), use it directly
    if (Array.isArray(updatedData)) {
      console.log("Received array data, setting directly:", updatedData);
      setEmailData(updatedData);
      return;
    }

    setEmailData(prevData => {
      // If prevData is an array, handle differently
      if (Array.isArray(prevData)) {
        console.log("Merging into existing array");
        return prevData;
      }
      
      const newData = { ...prevData };

      // Check if updatedData is an object with properties
      if (typeof updatedData === 'object' && updatedData !== null) {
        // Merge the updated data with existing data
        Object.keys(updatedData).forEach(key => {
          if (key === 'featured_products' && updatedData[key].features) {
            // Handle featured products specially
            if (!newData.featured_products) {
              newData.featured_products = { features: [] };
            }
            newData.featured_products.features = updatedData[key].features;
          } else {
            // Replace or add the section data
            newData[key] = {
              ...newData[key],
              ...updatedData[key]
            };
          }
        });
      }

      console.log("Updated email data:", newData);
      return newData;
    });
  }, []);

  useEffect(() => {
    if (!initialProductData) {
      const fetchProductData = async () => {
        try {
          setIsLoading(false);
        } catch (err) {
          setError(err.message);
          console.error("Error fetching product data:", err);
          setIsLoading(false);
        }
      };
      fetchProductData();
    }
  }, []);
  const convertToArray = (data) => {
    // If already an array, return as-is (preserving section IDs)
    if (Array.isArray(data)) {
      return data;
    }
    // If object, convert to array (legacy support)
    return Object.keys(data)
      .filter((key) => !isNaN(key))
      .map((key) => data[key]);
  };

  // Show the draft modal to let user choose update or create new
  const handleShowDraftModal = () => {
    setShowLocalDraftModal(true);
  };

  // Handle local draft modal confirmation (update existing or create new)
  const handleLocalDraftConfirm = async (draftChoice, newDraftTitle) => {
    console.log("Local draft modal confirmed:", draftChoice, newDraftTitle);
    
    if (draftChoice === "update") {
      // Update existing draft with current name
      await performSaveAsDraft(templateName, true);
    } else if (draftChoice === "new") {
      // Create new draft with new name
      console.log("Creating new email draft with title:", newDraftTitle);
      setTemplateName(newDraftTitle);
      await performSaveAsDraft(newDraftTitle, true);
    }
    
    // Close the modal
    setShowLocalDraftModal(false);
  };

  // Perform the actual save operation
  const performSaveAsDraft = async (nameToSave, showSuccessToast = true) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
  
      const modifiedEmailData = convertToArray(emailData);
  
      console.log(streamingSections);
      console.log(location.state);
      console.log(emailData);
  
      // 1. foray_response (stringified JSON)
      formData.append(
        "email_template[foray_response]",
        JSON.stringify({
          data: modifiedEmailData,
          styleSettings: {
            primaryColor,
            secondaryColor,
            backgroundColor,
            fontFamily,
          },
        })
      );
  
      // 2. name - use the passed name
      formData.append("email_template[name]", nameToSave || "Draft Email");
  
      // 3. tone
      formData.append("email_template[tone]", tone);
  
      // 4. preview_url
      formData.append("email_template[preview_url]", previewUrl);
  
      // 5. occasion
      formData.append("email_template[occasion]", occasion);
  
      // 6. further_details
      formData.append("email_template[further_details]", furtherDetails);
  
      // 7. status
      formData.append("email_template[status]", "draft");
  
      // 8. uid
      formData.append("email_template[uid]", uid);
  
      // Helper to convert base64 to File
      function base64ToFile(base64String, filename, mimeType) {
        const byteString = atob(base64String.split(",")[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        return new File([ab], filename, { type: mimeType });
      }

      // Helper to process and append an image to FormData
      const processAndAppendImage = (imageValue, imageName, sectionIndex) => {
        if (!imageValue) return;
        
        let imageFile = imageValue;
        
        // Handle File objects directly
        if (imageValue instanceof File) {
          formData.append("email_template[images][]", imageFile);
          formData.append("email_template[image_names][]", imageName);
          return;
        }
        
        // Handle base64 images
        if (typeof imageValue === "string" && imageValue.startsWith("data:image/")) {
          const mimeMatch = imageValue.match(/^data:(image\/[a-zA-Z]+);base64,/);
          const mimeType = mimeMatch ? mimeMatch[1] : "image/png";
          const extension = mimeType.split("/")[1];
          const filename = `${imageName}_${sectionIndex}.${extension}`;
          imageFile = base64ToFile(imageValue, filename, mimeType);
          formData.append("email_template[images][]", imageFile);
          formData.append("email_template[image_names][]", imageName);
          return;
        }
        
        // Skip regular URL images - they're already stored on server
        if (typeof imageValue === "string" && !imageValue.startsWith("data:")) {
          console.log("Skipping already-uploaded image URL:", imageValue);
          return;
        }
      };

      // 9. images and image_names
      // Use section.id as unique identifier to prevent conflicts between multiple sections of same type
      for (let i = 0; i < modifiedEmailData.length; i++) {
        const section = modifiedEmailData[i];
        // Use section.id for unique naming (prevents conflicts between multiple sections of same component type)
        const sectionId = section?.id || `section_${i}`;
  
        // Handle main image field
        if (section?.image) {
          processAndAppendImage(section.image, sectionId, i);
        }
        
        // Handle Title_4Img component's multiple image fields (image1, image2, image3, image4)
        ['image1', 'image2', 'image3', 'image4'].forEach((imgKey, imgIndex) => {
          if (section?.[imgKey]) {
            processAndAppendImage(section[imgKey], `${sectionId}_${imgKey}`, i);
          }
        });
        
        // Handle features array images (for components like 2COL_3ROWS_Benefits)
        if (section?.features && Array.isArray(section.features)) {
          section.features.forEach((feature, featureIndex) => {
            // Process image field (preferred)
            if (feature?.image) {
              processAndAppendImage(feature.image, `${sectionId}_feature_${featureIndex}_image`, i);
            }
            // Also process image_placeholder if image is not set
            else if (feature?.image_placeholder) {
              processAndAppendImage(feature.image_placeholder, `${sectionId}_feature_${featureIndex}_image`, i);
            }
          });
        }
      }
  
      // 10. Send the request
      const response = await axiosInstance.post(
        "/api/v1/email_templates",
        formData
      );
  
      if (response.data && response.data.template) {
        // Store the ID for future reference
        localStorage.setItem("last_saved_draft_id", response.data.template.id);
        
        // Update local state with the saved values
        setDraftSaved(true);
        
        // Update template name state
        setTemplateName(nameToSave || "Draft Email");
        
        if (showSuccessToast) {
          toast.success("Draft saved successfully");
        }
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft. Please try again.");
    }
  
    setIsLoading(false);
  };

  // Preview function - now with sections parameter
  const handlePreview = (sectionsFromContext, mode) => {
    if (!sectionsFromContext || !Array.isArray(sectionsFromContext) || sectionsFromContext.length === 0) {
      toast.error("No content available to preview. Please generate some content first.");
      return;
    }

    // Check if draft has been saved first
    if (!draftSaved) {
      toast.error("Please save your email as a draft first before previewing.");
      return;
    }


    // Add style settings
    const emailDataWithStyles = {
      ...sectionsFromContext,
      styleSettings: {
        primaryColor,
        secondaryColor,
        backgroundColor,
        fontFamily
      }
    };

    const updatedEmailTemplate = emailTemplateHTML(emailDataWithStyles);
    console.log("Navigating to preview with mode:", mode);
    navigate("/skills/email/preview-email", {
      state: {
        emailTemplateHTMLresponse: updatedEmailTemplate,
        mode: mode || "desktop",
        emailData: sectionsFromContext,
        templateId: templateId,
        sku: initialSku,
        templateName: templateName,
        primaryColor,
        secondaryColor,
        backgroundColor,
        fontFamily
      },
    });
  };

  // Load draft data if available
  // Load draft data if available
  // Inside the useEffect that loads the saved draft
useEffect(() => {
  const draftId = localStorage.getItem("last_saved_draft_id");

  if (draftId) {
    const fetchSavedDraft = async () => {
      try {
        const response = await axiosInstance.get(`/api/v1/email_templates/${draftId}`);
        const savedDraft = response.data;
        console.log("Draft data:", savedDraft);

        if (savedDraft) {
          setTone(savedDraft.tone || "");
          setOccasion(savedDraft.occasion || "");
          setFurtherDetails(savedDraft.further_details || "");
          setPreviewImage(savedDraft.preview_url || "");
          setTemplateName(savedDraft.name || "");

          // Important change here - process sections and match with attachments
          if (savedDraft.foray_response?.data && Array.isArray(savedDraft.foray_response.data)) {
            // Create a map of attachments by component_name for easy lookup
            const attachmentMap = {};
            if (savedDraft.attachments && Array.isArray(savedDraft.attachments)) {
              savedDraft.attachments.forEach(attachment => {
                if (attachment.name && attachment.image && attachment.image.url) {
                  attachmentMap[attachment.name] = attachment.image.url;
                }
              });
            }

            // Process each section to add images from attachments and clean up invalid image values
            const processedSections = savedDraft.foray_response.data.map((section, index) => {
              const newSection = { ...section };
              // Use section.id for unique lookup (same as save logic)
              const sectionId = section.id || `section_${index}`;
              
              // If this section has a matching attachment by section id
              if (sectionId && attachmentMap[sectionId]) {
                newSection.image = attachmentMap[sectionId];
              } else {
                // Clean up the image field if it's an invalid value
                newSection.image = cleanImageValue(section.image);
              }
              
              // Handle image1, image2, image3, image4 fields (for Title_4Img component)
              // These are saved with names like "sectionId_image1", "sectionId_image2", etc.
              ['image1', 'image2', 'image3', 'image4'].forEach(imgKey => {
                const attachmentKey = `${sectionId}_${imgKey}`;
                if (attachmentMap[attachmentKey]) {
                  // Found matching attachment - use the URL
                  newSection[imgKey] = attachmentMap[attachmentKey];
                } else if (section[imgKey] !== undefined) {
                  // No attachment found - clean up the existing value
                  newSection[imgKey] = cleanImageValue(section[imgKey]);
                }
              });
              
              // Also process nested features if they exist
              if (section.features && Array.isArray(section.features)) {
                newSection.features = section.features.map((feature, featureIndex) => {
                  const newFeature = { ...feature };
                  const featureImageKey = `${sectionId}_feature_${featureIndex}_image`;
                  
                  // Try to find matching attachment using the naming convention
                  if (attachmentMap[featureImageKey]) {
                    newFeature.image = attachmentMap[featureImageKey];
                    newFeature.image_placeholder = attachmentMap[featureImageKey];
                  } else if (feature.component_name && attachmentMap[feature.component_name]) {
                    // Fallback to component_name matching (for backward compatibility)
                    newFeature.image = attachmentMap[feature.component_name];
                    newFeature.image_placeholder = attachmentMap[feature.component_name];
                  } else {
                    // Clean up invalid image values
                    newFeature.image = cleanImageValue(feature.image || feature.image_placeholder);
                    newFeature.image_placeholder = cleanImageValue(feature.image_placeholder || feature.image);
                  }
                  return newFeature;
                });
              }
              
              return newSection;
            });

            // Update initialSections with the processed sections
            setInitialSections(processedSections);
            
            // Still set emailData for backward compatibility
            setEmailData(processedSections);
            
            // Mark that we've loaded saved data
            setDraftSaved(true);

            // Get style settings from saved draft if available
            if (savedDraft.foray_response.styleSettings) {
              setPrimaryColor(savedDraft.foray_response.styleSettings.primaryColor || "#333333");
              setSecondaryColor(savedDraft.foray_response.styleSettings.secondaryColor || "#a1894c");
              setBackgroundColor(savedDraft.foray_response.styleSettings.backgroundColor || "#ffffff");
              setFontFamily(savedDraft.foray_response.styleSettings.fontFamily ||
                "helvetica, 'helvetica neue', arial, verdana, sans-serif");
            }
          }
        }
      } catch (error) {
        console.error("Error loading saved draft:", error);
        localStorage.removeItem("last_saved_draft_id");
      }
    };

    fetchSavedDraft();
  }
}, []);

  // Handle color picker toggling
  const toggleColorPicker = (type) => {
    setActiveColorPicker(type);
    setShowColorPicker(!showColorPicker);
  };

  // Toggle style settings panel
  const toggleStyleSettings = () => {
    setShowStyleSettings(!showStyleSettings);
  };

  // Handle color change
  const handleColorChange = (color) => {
    if (activeColorPicker === 'primary') {
      setPrimaryColor(color.hex);
    } else if (activeColorPicker === 'secondary') {
      setSecondaryColor(color.hex);
    } else if (activeColorPicker === 'background') {
      setBackgroundColor(color.hex);
    }
  };

  // Handle font family change
  const handleFontChange = (fontValue) => {
    setFontFamily(fontValue);
  };

  const [streamingSections, setStreamingSections] = useState([]);
  const handleEmailChunk = (chunk) => {
    setStreamingSections(prev => [...prev, chunk]);
  };

  if (!initialSections || initialSections.length === 0) {
    return <Loader />; // or a message
  }

  return (
    <div>
      <Breadcrumb />
      <div className="d-flex justify-content-between">
        <div className="title-label mx-3">Edit Email</div>
        <div className="btns d-flex gap-3">
          {/* Style Settings Toggle Button */}
          <button
            className="btn btn-outline-secondary style-settings-toggle"
            onClick={toggleStyleSettings}
            title="Email Style Settings"
          >
            <Settings size={18} />
          </button>
        </div>
      </div>

      {/* Style Settings Panel */}
      {showStyleSettings && (
        <div
          ref={styleSettingsRef}
          className="style-settings-panel"
          style={{
            position: 'absolute',
            right: '20px',
            top: '120px',
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
            width: '280px'
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="m-0">Style Settings</h5>
            <button
              className="btn btn-sm"
              onClick={toggleStyleSettings}
              style={{ padding: '2px', lineHeight: 1 }}
            >
              <X size={18} />
            </button>
          </div>

          <div className="mb-3">
            <label className="form-label">Primary Color</label>
            <div
              className="color-preview d-flex align-items-center"
              onClick={() => toggleColorPicker('primary')}
              style={{ cursor: 'pointer' }}
            >
              <div
                style={{
                  backgroundColor: primaryColor,
                  width: '30px',
                  height: '30px',
                  borderRadius: '4px',
                  marginRight: '10px',
                  border: '1px solid #ddd'
                }}
              ></div>
              <span>{primaryColor}</span>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Secondary Color</label>
            <div
              className="color-preview d-flex align-items-center"
              onClick={() => toggleColorPicker('secondary')}
              style={{ cursor: 'pointer' }}
            >
              <div
                style={{
                  backgroundColor: secondaryColor,
                  width: '30px',
                  height: '30px',
                  borderRadius: '4px',
                  marginRight: '10px',
                  border: '1px solid #ddd'
                }}
              ></div>
              <span>{secondaryColor}</span>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Background Color</label>
            <div
              className="color-preview d-flex align-items-center"
              onClick={() => toggleColorPicker('background')}
              style={{ cursor: 'pointer' }}
            >
              <div
                style={{
                  backgroundColor: backgroundColor,
                  width: '30px',
                  height: '30px',
                  borderRadius: '4px',
                  marginRight: '10px',
                  border: '1px solid #ddd'
                }}
              ></div>
              <span>{backgroundColor}</span>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Font Family</label>
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-font-family" className="w-100 text-start">
                {fontOptions.find(font => font.value === fontFamily)?.name || 'Helvetica'}
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100">
                {fontOptions.map(font => (
                  <Dropdown.Item
                    key={font.value}
                    onClick={() => handleFontChange(font.value)}
                    style={{ fontFamily: font.value }}
                  >
                    {font.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Color Picker Popover */}
          {showColorPicker && (
            <div
              ref={colorPickerRef}
              style={{
                position: 'absolute',
                zIndex: 2,
                top: activeColorPicker === 'primary' ? '95px' : activeColorPicker === 'secondary' ? '155px' : '215px',
                left: '15px'
              }}
            >
              <ChromePicker
                color={
                  activeColorPicker === 'primary' 
                    ? primaryColor 
                    : activeColorPicker === 'secondary' 
                    ? secondaryColor 
                    : backgroundColor
                }
                onChange={handleColorChange}
                disableAlpha
              />
            </div>
          )}
        </div>
      )}

      <div className="container-fluid mt-3" >
       
        <div className="row gap-2">
          <div className="col-4 px-0">
            <AddEmailSidebar
              keywordsConfig={{
                value: keywords,
                onChange: (newKeywords) => {
                  console.log("Keywords being updated to:", newKeywords);
                  if (Array.isArray(newKeywords)) {
                    setKeywords(newKeywords);
                  }
                },
                label: "What are the occasion you want to include ",
                placeholder: "Enter custom occasion",
                optimizeButtonText: "Optimize",
              }}
              textareaConfig={{
                value: details,
                onChange: setDetails,
                label: "Further details that you would like included here",
                placeholder: "Enter custom details",
                rows: 5,
              }}
              emailData={emailData}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              sku={initialSku}
              furtherDetails={furtherDetails}
              setFurtherDetails={setFurtherDetails}
              tone={tone}
              setTone={setTone}
              occasion={occasion}
              setOccasion={setOccasion}
              // setEmailData={setEmailData}
              previewImage={previewImage}
              templateName={templateName}
              onEmailChunk={handleEmailChunk}
            />
          </div>
          <div className="col-auto px-0" style={{ borderRight: "2px dashed #ccc" }}></div>

          {/* Use EmailSectionProvider with key to ensure it remounts when initialSections changes */}
          <EmailSectionProvider key={JSON.stringify(initialSections)} initialSections={initialSections}>
            <EmailContent
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              backgroundColor={backgroundColor}
              fontFamily={fontFamily}
              onSaveDraft={handleShowDraftModal}
              onPreview={handlePreview}
              emailData={emailData}
              isLoading={isLoading}
              onUpdateEmailData={handleUpdateEmailData}
              draftSaved={draftSaved}
            />
          </EmailSectionProvider>
        </div>
      </div>

      <LocalDraftModal
        show={showLocalDraftModal}
        onHide={() => setShowLocalDraftModal(false)}
        onConfirm={handleLocalDraftConfirm}
        isChecking={isLoading}
        showUpdateOption={draftSaved}
      />
    </div>
  );
};

export default EditEmail;